import { FormattedMessage } from 'react-intl';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAuth } from '../features/auth/useAuth';
import { areaOfPath, rememberLocation } from '../utils/lastLocation';

/**
 * An address no route knows — a bookmark from an older version, a mistyped or
 * cut-off link. Says so instead of an empty page, with a way back to the start
 * of the app the address was in.
 *
 * Shown at the top level and inside each app, so the office drawer and the
 * field bar stay where they are.
 */
export default function NotFoundPage() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const area = areaOfPath(pathname);
    const start = area ? `/${area}` : '/';

    return (
        <Box
            sx={{
                minHeight: '60vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                px: 2,
                textAlign: 'center'
            }}
        >
            <Typography variant="h6">
                <FormattedMessage id="notFound.title" />
            </Typography>
            <Typography sx={{ maxWidth: 480, color: 'text.secondary' }}>
                <FormattedMessage id="notFound.message" />
            </Typography>
            <Button
                variant="outlined"
                component={RouterLink}
                to={start}
                onClick={() => {
                    // This address was remembered as the place to reopen at
                    // (useRememberLocation), and an app's start page never
                    // is — so it would come back on the next launch.
                    if (user && area) {
                        rememberLocation(user.id, start);
                    }
                }}
            >
                <FormattedMessage id="notFound.toStart" />
            </Button>
        </Box>
    );
}
